import { supabase } from '../lib/supabase';
import { invokeGovernance, invokeOnboardingAdmin, invokePartnerAdmin, invokePayoutRoll } from '../lib/adminClient';
import type { AdminClawback, AdminCommissionPlan, AdminOnboardingApplication, AdminOverview, AdminPartner, AdminPayout, AdminWorkspaceData } from './types';

/* eslint-disable @typescript-eslint/no-explicit-any */

// Staff-gated program reads. The RPCs check is_staff() in Postgres; the browser
// only ever holds the anon key + the operator's JWT.
export async function loadAdminWorkspace(): Promise<AdminWorkspaceData> {
  const [overviewResult, partnersResult, clawbacksResult, payoutsResult, onboardingResult] = await Promise.all([
    supabase.rpc('partner_os_admin_overview'),
    supabase.rpc('partner_os_admin_partners'),
    supabase.rpc('partner_os_admin_clawbacks'),
    supabase.rpc('partner_os_admin_payouts'),
    supabase.rpc('partner_os_admin_onboarding_queue'),
  ]);
  for (const result of [overviewResult, partnersResult, clawbacksResult, payoutsResult, onboardingResult]) {
    if (result.error) throw new Error(result.error.message);
  }

  const o = overviewResult.data as any;
  const overview: AdminOverview = {
    totalPartners: Number(o.totalPartners),
    activePartners: Number(o.activePartners),
    availableCents: Number(o.availableCents),
    paidCents: Number(o.paidCents),
    pendingClawbacks: Number(o.pendingClawbacks),
    referredBuilders: Number(o.referredBuilders),
  };

  const partners: AdminPartner[] = ((partnersResult.data ?? []) as any[]).map((p) => ({
    partnerId: p.partner_id,
    handle: p.handle,
    displayName: p.display_name,
    tier: p.tier,
    status: p.status,
    lifetimeCents: Number(p.lifetime_cents),
    availableCents: Number(p.available_cents),
    referredCount: Number(p.referred_count),
    joinedAt: p.joined_at,
  }));

  const clawbacks: AdminClawback[] = ((clawbacksResult.data ?? []) as any[]).map((c) => ({
    id: c.id,
    partnerId: c.partner_id,
    handle: c.handle,
    eventType: c.event_type,
    requestedGrossCents: c.requested_gross_cents == null ? null : Number(c.requested_gross_cents),
    appliedPartnerEarningCents: c.applied_partner_earning_cents == null ? null : Number(c.applied_partner_earning_cents),
    status: c.status,
    reviewHoldUntil: c.review_hold_until ?? null,
    disputeUntil: c.dispute_until ?? null,
    reason: c.reason,
    createdAt: c.created_at,
  }));

  const payouts: AdminPayout[] = ((payoutsResult.data ?? []) as any[]).map((p) => ({
    id: p.id,
    partnerId: p.partner_id,
    handle: p.handle,
    periodStart: p.period_start,
    periodEnd: p.period_end,
    amountCents: Number(p.amount_cents),
    status: p.status,
    issuedAt: p.issued_at ?? null,
  }));

  const onboarding: AdminOnboardingApplication[] = ((onboardingResult.data ?? []) as any[]).map((a) => ({
    applicationId: a.application_id,
    partnerId: a.partner_id ?? null,
    displayName: a.display_name,
    email: a.email,
    status: a.status,
    currentStage: a.current_stage,
    ownerRole: a.owner_role,
    slaDueAt: a.sla_due_at ?? null,
    slaOverdue: Boolean(a.sla_overdue),
    missingRequirements: a.missing_requirements ?? [],
  }));

  return { overview, partners, clawbacks, payouts, onboarding };
}

// Run a payout roll for a period through the staff-gated admin-payouts function
// (service-role-only roll happens server-side; the browser never sees it).
export async function runPayoutRoll(periodStart: string, periodEnd: string): Promise<void> {
  await invokePayoutRoll({ periodStart, periodEnd });
}

// Approved commission plans, for the assign-plan dropdown (staff-gated read).
export async function loadCommissionPlans(): Promise<AdminCommissionPlan[]> {
  const { data, error } = await supabase.rpc('partner_os_admin_commission_plans');
  if (error) throw new Error(error.message);
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  return ((data ?? []) as any[]).map((p) => ({
    planVersionId: p.plan_version_id,
    internalName: p.internal_name,
    displayName: p.display_name,
    versionNumber: Number(p.version_number),
    minimumPayoutCents: Number(p.minimum_payout_cents),
  }));
}

// ---- operator actions (Phase C) --------------------------------------------
// All terminate at the staff-gated admin-governance / admin-partners Edge
// Functions. The service-role key never reaches the browser.

export async function setPartnerStatus(partnerId: string, status: string): Promise<void> {
  await invokeGovernance({ action: 'set_partner_status', partnerId, status });
}

export async function assignCommissionPlan(partnerId: string, planVersionId: string): Promise<void> {
  await invokeGovernance({ action: 'assign_plan', partnerId, planVersionId });
}

export async function approveClawback(eventId: string): Promise<void> {
  await invokeGovernance({ action: 'approve_clawback', eventId });
}

export async function rejectClawback(eventId: string, reason: string): Promise<void> {
  await invokeGovernance({ action: 'reject_clawback', eventId, reason });
}

export async function createPartner(input: {
  handle: string;
  displayName: string;
  email: string;
  tier: 'partner' | 'founding';
}): Promise<void> {
  await invokePartnerAdmin({ action: 'create_partner', ...input });
}

export async function mintPartnerCode(partnerId: string, code: string): Promise<void> {
  await invokePartnerAdmin({ action: 'mint_code', partnerId, code });
}

export async function reviewPartnerApplication(applicationId: string, decision: 'approve' | 'reject', reason = ''): Promise<void> {
  await invokeOnboardingAdmin({ action: 'review_application', applicationId, decision, reason, confirmed: true });
}

export async function issuePartnerOffer(applicationId: string, planVersionId: string, personalizedSummary: string): Promise<void> {
  await invokeOnboardingAdmin({ action: 'issue_offer', applicationId, planVersionId, personalizedSummary, confirmed: true });
}

export async function setOnboardingRequirement(partnerId: string, requirementKey: string, state: 'pending' | 'complete'): Promise<void> {
  await invokeOnboardingAdmin({ action: 'set_onboarding_requirement', partnerId, requirementKey, state, confirmed: true });
}

export async function transitionPartnerProgram(partnerId: string, toStatus: string, reason: string): Promise<void> {
  await invokeOnboardingAdmin({ action: 'transition_partner', partnerId, toStatus, reason, confirmed: true });
}

export async function issueOnboardingReferralCode(partnerId: string, code: string): Promise<void> {
  await invokeOnboardingAdmin({ action: 'issue_referral_code', partnerId, code, confirmed: true });
}
